import { useState, type FormEvent } from 'react';
import { ErreurApi, ErreurReseau } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { SaisiesSurTelephone } from '../composants/SaisiesSurTelephone';
import { useFile } from '../horsligne/contexte-file';
import { MESSAGE_COMPTE_DESACTIVE } from '../horsligne/textes';

/**
 * Écran de connexion. Il dit aussi pourquoi on y est (pas de réseau, session
 * terminée, déconnexion pas encore reçue par le serveur) et combien de saisies
 * attendent sur le téléphone : des nombres seulement, jamais de contenu ni de
 * compte (docs/UI.md § Écran 3, mode hors ligne).
 */
export function Connexion() {
  const { connecter, etat, motifFin, deconnexionDifferee, reessayer } = useAuth();
  const { instantane } = useFile();
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await connecter(email.trim(), motDePasse);
    } catch (e) {
      // Le serveur ne distingue pas compte inconnu et mot de passe faux :
      // l'interface n'en dit pas plus que lui.
      setErreur(
        e instanceof ErreurApi && e.statut === 401
          ? 'Identifiants invalides.'
          : e instanceof ErreurReseau
            ? 'Pas de réseau : connexion impossible pour l’instant.'
            : e instanceof Error
              ? e.message
              : 'Connexion impossible.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <main className="connexion">
      <form className="carte" onSubmit={soumettre}>
        <img src="/logo-plaisirs-gourmands.png" alt="Plaisirs Gourmands" className="logo-connexion logo-clair" />
        <img src="/logo-plaisirs-gourmands-sombre.png" alt="Plaisirs Gourmands" className="logo-connexion logo-sombre" />
        <h1>
          RH <span>Plaisirs Gourmands</span>
        </h1>

        {etat === 'sans_reseau' && (
          <div className="signal" role="status">
            <p>Pas de réseau. Votre session reprendra toute seule au retour du réseau, sans mot de passe.</p>
            <SaisiesSurTelephone recensement={instantane.recensement} contexte="sans_reseau" />
            <button type="button" className="bouton-second" onClick={reessayer}>
              Réessayer maintenant
            </button>
          </div>
        )}
        {etat === 'session_perdue' && (
          <div className="signal" role="status">
            <p>Votre session a pris fin : reconnectez-vous.</p>
            {motifFin && <p className="mention">{motifFin}</p>}
            <SaisiesSurTelephone recensement={instantane.recensement} contexte="session_perdue" />
          </div>
        )}
        {instantane.compteDesactive && (
          <p className="signal" role="status">
            {MESSAGE_COMPTE_DESACTIVE}
          </p>
        )}
        {deconnexionDifferee && (
          <p className="signal" role="status">
            Vous êtes déconnecté sur ce téléphone. La session sera aussi fermée sur le serveur au retour du réseau.
          </p>
        )}

        <label htmlFor="email">Adresse e-mail</label>
        <input
          id="email"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="motDePasse">Mot de passe</label>
        <input
          id="motDePasse"
          type="password"
          name="motDePasse"
          autoComplete="current-password"
          required
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
        />

        {erreur && (
          <p className="erreur" role="alert">
            {erreur}
          </p>
        )}

        <button type="submit" disabled={envoi}>
          {envoi ? 'Connexion…' : 'Se connecter'}
        </button>

        <p className="mention">
          Comptes nominatifs. Les connexions sont journalisées.
        </p>
      </form>
    </main>
  );
}
